import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { 
  FiPlus, FiEdit2, FiTrash2, FiSave, FiX, FiTrendingUp, FiTrendingDown, 
  FiDollarSign, FiDownload, FiCalendar, FiFilter 
} from 'react-icons/fi';
import './ManajemenKeuangan.css';

const ManajemenKeuangan = () => {
  const [transaksiList, setTransaksiList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState({
    jenis_transaksi: 'Masuk',
    kategori: '',
    nominal: '',
    tanggal: new Date().toISOString().split('T')[0],
    deskripsi: '',
    keterangan: ''
  });
  const [selectedTransaksi, setSelectedTransaksi] = useState(null);
  const [filterJenis, setFilterJenis] = useState('');
  const [filterBulan, setFilterBulan] = useState('');
  const [filterTahun, setFilterTahun] = useState(new Date().getFullYear().toString());

  const kategoriMasuk = [
    'Pembayaran SPP',
    'Uang Pangkal',
    'Uang Gedung',
    'Praktikum',
    'Ujian',
    'Wisuda',
    'Denda',
    'Lain-lain'
  ];

  const kategoriKeluar = [
    'Gaji Dosen',
    'Gaji Staff',
    'Operasional',
    'Pemeliharaan Gedung',
    'Utilitas (Listrik, Air)',
    'Alat Tulis Kantor',
    'Pembelian Aset',
    'Perawatan Peralatan',
    'Konsumsi',
    'Transportasi',
    'Lain-lain'
  ];

  const bulanOptions = [
    'Januari', 'Februari', 'Maret', 'April', 'Mei', 'Juni',
    'Juli', 'Agustus', 'September', 'Oktober', 'November', 'Desember'
  ];

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/finance/manajemen-keuangan');
      setTransaksiList(res.data.data);
    } catch (error) {
      toast.error('Gagal mengambil data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleOpenModal = (transaksi = null) => {
    if (transaksi) {
      setEditMode(true);
      setSelectedTransaksi(transaksi);
      setFormData({
        jenis_transaksi: transaksi.jenis_transaksi,
        kategori: transaksi.kategori,
        nominal: transaksi.nominal,
        tanggal: transaksi.tanggal.split('T')[0],
        deskripsi: transaksi.deskripsi || '',
        keterangan: transaksi.keterangan || ''
      });
    } else {
      setEditMode(false);
      setSelectedTransaksi(null);
      setFormData({
        jenis_transaksi: 'Masuk',
        kategori: '',
        nominal: '',
        tanggal: new Date().toISOString().split('T')[0],
        deskripsi: '',
        keterangan: ''
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditMode(false);
    setSelectedTransaksi(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Reset kategori saat jenis transaksi berubah
    if (name === 'jenis_transaksi') {
      setFormData(prev => ({ ...prev, kategori: '' }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.kategori || !formData.nominal) {
      toast.error('Mohon lengkapi data yang wajib diisi');
      return;
    }

    try {
      if (editMode) {
        await axios.put(`/api/finance/manajemen-keuangan/${selectedTransaksi._id}`, formData);
        toast.success('Transaksi berhasil diupdate');
      } else {
        await axios.post('/api/finance/manajemen-keuangan', formData);
        toast.success('Transaksi berhasil ditambahkan');
      }
      
      handleCloseModal();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Terjadi kesalahan');
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Apakah Anda yakin ingin menghapus transaksi ini?')) {
      try {
        await axios.delete(`/api/finance/manajemen-keuangan/${id}`);
        toast.success('Transaksi berhasil dihapus');
        fetchData();
      } catch (error) {
        toast.error('Gagal menghapus transaksi');
      }
    }
  };

  const handleCetakRekap = async () => {
    try {
      const params = new URLSearchParams();
      if (filterBulan) params.append('bulan', bulanOptions.indexOf(filterBulan) + 1);
      if (filterTahun) params.append('tahun', filterTahun);
      
      const res = await axios.get(`/api/finance/rekap-pembiayaan?${params.toString()}`);
      
      // Create print content
      const printWindow = window.open('', '_blank');
      printWindow.document.write(generateRekapHTML(res.data.data));
      printWindow.document.close();
      printWindow.print();
    } catch (error) {
      toast.error('Gagal membuat rekap');
    }
  };

  const generateRekapHTML = (data) => {
    const periode = filterBulan ? `${filterBulan} ${filterTahun}` : filterTahun;
    
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Rekap Pembiayaan - ${periode}</title>
        <style>
          body { font-family: Arial, sans-serif; padding: 20px; }
          h1 { text-align: center; color: #2c3e50; }
          h2 { color: #3498db; border-bottom: 2px solid #3498db; padding-bottom: 5px; }
          table { width: 100%; border-collapse: collapse; margin: 20px 0; }
          th, td { border: 1px solid #ddd; padding: 10px; text-align: left; }
          th { background: #3498db; color: white; }
          .summary { background: #ecf0f1; padding: 15px; border-radius: 5px; margin: 20px 0; }
          .total { font-weight: bold; font-size: 18px; }
          .masuk { color: #27ae60; }
          .keluar { color: #e74c3c; }
          .saldo { color: #2c3e50; font-size: 20px; }
          @media print {
            button { display: none; }
          }
        </style>
      </head>
      <body>
        <h1>REKAP PEMBIAYAAN</h1>
        <h2>Periode: ${periode}</h2>
        
        <div class="summary">
          <table>
            <tr>
              <td><strong>Total Uang Masuk:</strong></td>
              <td class="masuk total">${formatRupiah(data.totalMasuk)}</td>
            </tr>
            <tr>
              <td><strong>Total Uang Keluar:</strong></td>
              <td class="keluar total">${formatRupiah(data.totalKeluar)}</td>
            </tr>
            <tr>
              <td><strong>Saldo:</strong></td>
              <td class="saldo total">${formatRupiah(data.saldo)}</td>
            </tr>
          </table>
        </div>
        
        <h2>Rincian Transaksi</h2>
        <table>
          <thead>
            <tr>
              <th>No</th>
              <th>Tanggal</th>
              <th>Jenis</th>
              <th>Kategori</th>
              <th>Deskripsi</th>
              <th>Nominal</th>
            </tr>
          </thead>
          <tbody>
            ${data.transaksi.map((t, i) => `
              <tr>
                <td>${i + 1}</td>
                <td>${formatTanggal(t.tanggal)}</td>
                <td>${t.jenis_transaksi}</td>
                <td>${t.kategori}</td>
                <td>${t.deskripsi || '-'}</td>
                <td class="${t.jenis_transaksi === 'Masuk' ? 'masuk' : 'keluar'}">
                  ${formatRupiah(t.nominal)}
                </td>
              </tr>
            `).join('')}
          </tbody>
        </table>
        
        <p style="margin-top: 40px; text-align: right;">
          <em>Dicetak pada: ${new Date().toLocaleString('id-ID')}</em>
        </p>
      </body>
      </html>
    `;
  };

  const formatRupiah = (angka) => {
    return new Intl.NumberFormat('id-ID', {
      style: 'currency',
      currency: 'IDR',
      minimumFractionDigits: 0
    }).format(angka);
  };

  const formatTanggal = (tanggal) => {
    return new Date(tanggal).toLocaleDateString('id-ID', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  const filteredTransaksi = transaksiList.filter(t => {
    const transaksiDate = new Date(t.tanggal);
    const matchJenis = !filterJenis || t.jenis_transaksi === filterJenis;
    const matchBulan = !filterBulan || transaksiDate.getMonth() === bulanOptions.indexOf(filterBulan);
    const matchTahun = !filterTahun || transaksiDate.getFullYear() === parseInt(filterTahun);
    return matchJenis && matchBulan && matchTahun;
  });

  const totalMasuk = filteredTransaksi
    .filter(t => t.jenis_transaksi === 'Masuk')
    .reduce((sum, t) => sum + t.nominal, 0);

  const totalKeluar = filteredTransaksi
    .filter(t => t.jenis_transaksi === 'Keluar')
    .reduce((sum, t) => sum + t.nominal, 0);

  const saldo = totalMasuk - totalKeluar;

  const currentYears = Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - i);

  if (loading) {
    return <div className="spinner"></div>;
  }

  return (
    <div className="manajemen-keuangan-page">
      {/* Header */}
      <div className="modern-header">
        <div className="header-background">
          <div className="header-pattern"></div>
        </div>
        <div className="header-content">
          <div className="header-text">
            <div className="header-icon-wrapper">
              <FiTrendingUp className="header-icon" />
            </div>
            <div>
              <h1>Manajemen Keuangan</h1>
              <p>Kelola uang masuk, uang keluar, dan saldo</p>
            </div>
          </div>
          <div className="header-actions">
            <button className="btn btn-success" onClick={handleCetakRekap}>
              <FiDownload /> Cetak Rekap
            </button>
            <button className="btn btn-glass" onClick={() => handleOpenModal()}>
              <FiPlus /> Tambah Transaksi
            </button>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="stats-grid stats-grid-4">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #10b981 0%, #059669 100%)' }}>
            <FiTrendingUp />
          </div>
          <div className="stat-details">
            <h3>{formatRupiah(totalMasuk)}</h3>
            <p>Total Uang Masuk</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' }}>
            <FiTrendingDown />
          </div>
          <div className="stat-details">
            <h3>{formatRupiah(totalKeluar)}</h3>
            <p>Total Uang Keluar</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' }}>
            <FiDollarSign />
          </div>
          <div className="stat-details">
            <h3 style={{ color: saldo >= 0 ? '#10b981' : '#ef4444' }}>
              {formatRupiah(saldo)}
            </h3>
            <p>Saldo</p>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon" style={{ background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)' }}>
            <FiCalendar />
          </div>
          <div className="stat-details">
            <h3>{filteredTransaksi.length}</h3>
            <p>Total Transaksi</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="card">
        <div className="filters">
          <div className="search-box">
            <FiFilter />
            <select 
              className="filter-select"
              value={filterJenis}
              onChange={(e) => setFilterJenis(e.target.value)}
            >
              <option value="">Semua Jenis</option>
              <option value="Masuk">Uang Masuk</option>
              <option value="Keluar">Uang Keluar</option>
            </select>
          </div>
          <div className="search-box">
            <select 
              className="filter-select"
              value={filterBulan}
              onChange={(e) => setFilterBulan(e.target.value)}
            >
              <option value="">Semua Bulan</option>
              {bulanOptions.map(bulan => (
                <option key={bulan} value={bulan}>{bulan}</option>
              ))}
            </select>
          </div>
          <div className="search-box">
            <select 
              className="filter-select"
              value={filterTahun}
              onChange={(e) => setFilterTahun(e.target.value)}
            >
              <option value="">Semua Tahun</option>
              {currentYears.map(year => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="card">
        <div className="modern-table-container">
          <table className="modern-table">
            <thead>
              <tr>
                <th>No</th>
                <th>Tanggal</th>
                <th>Jenis</th>
                <th>Kategori</th>
                <th>Deskripsi</th>
                <th>Nominal</th>
                <th>Keterangan</th>
                <th>Aksi</th>
              </tr>
            </thead>
            <tbody>
              {filteredTransaksi.length === 0 ? (
                <tr>
                  <td colSpan="8" style={{ textAlign: 'center', padding: '40px' }}>
                    Tidak ada data transaksi
                  </td>
                </tr>
              ) : (
                filteredTransaksi.map((transaksi, index) => (
                  <tr key={transaksi._id}>
                    <td>{index + 1}</td>
                    <td>{formatTanggal(transaksi.tanggal)}</td>
                    <td>
                      <span className={`badge ${transaksi.jenis_transaksi === 'Masuk' ? 'badge-success' : 'badge-danger'}`}>
                        {transaksi.jenis_transaksi}
                      </span>
                    </td>
                    <td>{transaksi.kategori}</td>
                    <td>{transaksi.deskripsi || '-'}</td>
                    <td>
                      <strong style={{ color: transaksi.jenis_transaksi === 'Masuk' ? '#10b981' : '#ef4444' }}>
                        {transaksi.jenis_transaksi === 'Masuk' ? '+' : '-'} {formatRupiah(transaksi.nominal)}
                      </strong>
                    </td>
                    <td>{transaksi.keterangan || '-'}</td>
                    <td>
                      <div className="table-actions">
                        <button
                          className="table-btn table-btn-edit"
                          onClick={() => handleOpenModal(transaksi)}
                          title="Edit"
                        >
                          <FiEdit2 />
                        </button>
                        <button
                          className="table-btn table-btn-delete"
                          onClick={() => handleDelete(transaksi._id)}
                          title="Hapus"
                        >
                          <FiTrash2 />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal Form */}
      {showModal && (
        <div className="modal-overlay" onClick={handleCloseModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editMode ? 'Edit Transaksi' : 'Tambah Transaksi Baru'}</h3>
              <button className="modal-close" onClick={handleCloseModal}>&times;</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="modal-body">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Jenis Transaksi <span className="required">*</span></label>
                    <select
                      name="jenis_transaksi"
                      value={formData.jenis_transaksi}
                      onChange={handleChange}
                      className="form-control"
                      required
                    >
                      <option value="Masuk">Uang Masuk</option>
                      <option value="Keluar">Uang Keluar</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Tanggal <span className="required">*</span></label>
                    <input
                      type="date"
                      name="tanggal"
                      value={formData.tanggal}
                      onChange={handleChange}
                      className="form-control"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Kategori <span className="required">*</span></label>
                    <select
                      name="kategori"
                      value={formData.kategori}
                      onChange={handleChange}
                      className="form-control"
                      required
                    >
                      <option value="">Pilih Kategori</option>
                      {(formData.jenis_transaksi === 'Masuk' ? kategoriMasuk : kategoriKeluar).map(kat => (
                        <option key={kat} value={kat}>{kat}</option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Nominal <span className="required">*</span></label>
                    <input
                      type="number"
                      name="nominal"
                      value={formData.nominal}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Masukkan nominal"
                      required
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Deskripsi</label>
                    <input
                      type="text"
                      name="deskripsi"
                      value={formData.deskripsi}
                      onChange={handleChange}
                      className="form-control"
                      placeholder="Deskripsi singkat"
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Keterangan</label>
                    <textarea
                      name="keterangan"
                      value={formData.keterangan}
                      onChange={handleChange}
                      className="form-control"
                      rows="2"
                      placeholder="Keterangan tambahan (opsional)"
                    />
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={handleCloseModal}>
                  <FiX /> Batal
                </button>
                <button type="submit" className="btn btn-primary">
                  <FiSave /> {editMode ? 'Update' : 'Simpan'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManajemenKeuangan;
